import { Link, Navigate, useParams } from "react-router-dom";
import { ChevronRight, ShieldCheck } from "lucide-react";
import { CtaBand } from "@/components/site/PageHero";
import { CategoryFaqs } from "@/components/catalog/CategorySections";
import { ModelsTable, QuoteButton, SpecsTable, UseChips } from "@/components/catalog/ProductParts";
import { CATEGORIES, SCOPES } from "@/data/products";
import { productDescription, productFaqs, productJsonLd, productStandards, productSummary, productTitle } from "@/data/seo";
import { SITE_URL, useSeo } from "@/lib/seo";

const findProduct = (id) => {
  for (const c of CATEGORIES) {
    const p = c.products.find((x) => x.id === id);
    if (p) return { p, c };
  }
  return null;
};

const Breadcrumb = ({ p, c }) => (
  <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1 text-xs text-slate-500" data-testid="product-breadcrumb">
    <Link to="/" className="hover:text-slate-900">Home</Link><ChevronRight className="h-3 w-3" />
    <Link to="/solutions" className="hover:text-slate-900">Solutions</Link><ChevronRight className="h-3 w-3" />
    <Link to={`/solutions/${c.slug}`} className="hover:text-slate-900" data-testid="product-breadcrumb-category">{c.name}</Link><ChevronRight className="h-3 w-3" />
    <span className="text-slate-900">{p.name}</span>
  </nav>
);

const Label = ({ children }) => <p className="mono mt-8 text-[10px] uppercase tracking-[0.18em] text-slate-400">{children}</p>;

const ProductInfo = ({ p, c }) => (
  <div>
    <p className="eyebrow" style={{ color: c.accent }}>{c.name}</p>
    <h1 className="mt-3 text-4xl leading-[1.08] text-slate-900 sm:text-5xl" data-testid="product-title">{p.name}</h1>
    <p className="mt-4 text-lg text-slate-600" data-testid="product-tagline">{p.tagline}</p>
    <p className="mt-5 text-sm leading-relaxed text-slate-600" data-testid="product-description">{productDescription(p, c)}</p>
    <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#F4F6F2] px-3 py-1 text-xs font-medium text-slate-700"><ShieldCheck className="h-3.5 w-3.5 text-[#1A4D4A]" /> {SCOPES[c.scope].label}</div>
    <div className="mt-8">
      <QuoteButton p={p} className="px-8 py-3.5" />
    </div>
    <p className="mt-3 text-xs text-slate-500">{p.models ? "Supplied under Vinsafe model codes." : "Makes & models proposed per site requirement."} A specialist replies with pricing within 1 business day.</p>
  </div>
);

const Models = ({ p }) => (
  <section className="container-x pb-16" data-testid="product-models">
    <Label>Available models · {p.models.length}</Label>
    {p.note && <p className="mt-2 max-w-3xl text-sm text-slate-600">{p.note}</p>}
    <div className="mt-4"><ModelsTable models={p.models} /></div>
    <p className="mt-3 text-xs text-slate-500">Sizes, load limits and weights are indicative; final figures are confirmed on the quotation.</p>
  </section>
);

const Details = ({ p, c }) => (
  <section className="container-x grid gap-10 pb-16 lg:grid-cols-2" data-testid="product-details">
    <div><Label>Specifications</Label><div className="mt-3"><SpecsTable specs={p.specs} testid="product-page-specs" /></div></div>
    <div>
      <Label>Where it's used</Label><div className="mt-3"><UseChips uses={p.uses} /></div>
      <Label>Industries</Label><div className="mt-3"><UseChips uses={c.industries} /></div>
      {productStandards(p, c).length > 0 && <><Label>Referenced standards</Label><div className="mt-3"><UseChips uses={productStandards(p, c)} /></div></>}
    </div>
  </section>
);

const Related = ({ p, c }) => {
  const others = c.products.filter((x) => x.id !== p.id);
  if (!others.length) return null;
  return (
    <section className="border-t border-slate-200 bg-[#F4F6F2] py-16" data-testid="product-related">
      <div className="container-x">
        <p className="eyebrow">More in {c.name}</p>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {others.map((o) => (
            <Link key={o.id} to={`/products/${o.id}`} className="card-base group flex items-center gap-4 p-4" data-testid={`related-product-${o.id}`}>
              <img src={o.image} alt={o.name} loading="lazy" className="h-16 w-16 shrink-0 rounded-lg bg-white object-contain mix-blend-multiply" />
              <p className="min-w-0 text-sm font-semibold text-slate-900 group-hover:text-[#C20500]">{o.name}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

const ProductView = ({ p, c }) => {
  useSeo({ title: productTitle(p, c), description: productSummary(p, c), path: `/products/${p.id}`, image: p.image, jsonLd: productJsonLd(SITE_URL, p, c) });
  return (
    <article data-testid={`product-page-${p.id}`}>
      <section className="container-x pb-12 pt-10">
        <Breadcrumb p={p} c={c} />
        <div className="mt-8 grid gap-10 lg:grid-cols-2 lg:items-center">
          <div className="flex items-center justify-center rounded-2xl border border-slate-200 bg-white p-8">
            <img src={p.image} alt={`${p.name}, ${p.tagline}`} className="max-h-[460px] w-full object-contain mix-blend-multiply" data-testid="product-image" />
          </div>
          <ProductInfo p={p} c={c} />
        </div>
      </section>
      <Details p={p} c={c} />
      {p.models && <Models p={p} />}
      <CategoryFaqs faqs={productFaqs(p, c)} />
      <Related p={p} c={c} />
      <CtaBand />
    </article>
  );
};

export default function ProductPage() {
  const { id } = useParams();
  const found = findProduct(id);
  if (!found) return <Navigate to="/solutions" replace />;
  return <ProductView key={id} p={found.p} c={found.c} />;
}
